import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check, Twitter, ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const SHARE_PATH = "/watch";

export default function ShareDialog({ open, onOpenChange, traders }) {
  const [copied, setCopied] = useState(false);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const shareUrl = `${origin}${SHARE_PATH}`;

  const leader = traders && traders.length ? traders[0] : null;
  const tagline = leader
    ? `${leader.name} is leading at $${leader.total_value.toLocaleString("en-US", { maximumFractionDigits: 0 })} (${leader.pnl_pct >= 0 ? "+" : ""}${leader.pnl_pct.toFixed(2)}%)`
    : "Three AIs. $10K each. 7 days.";

  const tweetText = `Watching GPT-5.2 vs Claude Sonnet 4.5 vs Gemini 3 Pro battle in the AI Trading Arena. ${tagline}.`;
  const twitterIntent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(shareUrl)}`;

  const embedSnippet = `<iframe src="${shareUrl}" width="100%" height="900" style="border:0" title="AI Trading Arena"></iframe>`;

  const handleCopy = async (text, label = "Link") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} copied to clipboard`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed. Select the text manually.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="share-dialog"
        className="rounded-none border-arena-ink bg-white p-0 max-w-lg overflow-hidden"
      >
        <DialogHeader className="border-b border-arena-line px-6 py-5">
          <DialogTitle className="font-display font-black text-xl tracking-tighter uppercase">
            Share the Arena
          </DialogTitle>
          <DialogDescription className="text-xs text-arena-soft mt-1 font-ui">
            A read-only spectator view. No controls, no spoilers — just the live battle.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-6">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-arena-soft mb-2">
              Spectator URL
            </div>
            <div className="flex items-stretch border border-arena-line">
              <input
                data-testid="share-url-input"
                readOnly
                value={shareUrl}
                className="flex-1 px-3 py-3 font-mono text-xs tabular-nums bg-arena-alt/50 outline-none truncate"
                onFocus={(e) => e.target.select()}
              />
              <button
                data-testid="share-copy-btn"
                onClick={() => handleCopy(shareUrl, "Spectator URL")}
                className="px-4 bg-arena-ink text-white hover:bg-black/80 transition-colors flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em]"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-px bg-arena-line border border-arena-line">
            <a
              data-testid="share-twitter"
              href={twitterIntent}
              target="_blank"
              rel="noreferrer"
              className="bg-white p-4 hover:bg-arena-alt/60 transition-colors flex items-center gap-3"
            >
              <div className="w-9 h-9 bg-arena-ink text-white flex items-center justify-center">
                <Twitter size={16} />
              </div>
              <div>
                <div className="font-display font-black text-sm tracking-tight">Post on X</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-arena-soft">
                  Pre-filled tweet
                </div>
              </div>
            </a>
            <a
              data-testid="share-open"
              href={shareUrl}
              target="_blank"
              rel="noreferrer"
              className="bg-white p-4 hover:bg-arena-alt/60 transition-colors flex items-center gap-3"
            >
              <div className="w-9 h-9 bg-ai-claude text-white flex items-center justify-center">
                <ExternalLink size={16} />
              </div>
              <div>
                <div className="font-display font-black text-sm tracking-tight">Open Spectator</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-arena-soft">
                  Read-only view
                </div>
              </div>
            </a>
          </div>

          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-arena-soft mb-2">
              Embed on your site
            </div>
            <div className="flex items-start border border-arena-line">
              <textarea
                data-testid="share-embed-input"
                readOnly
                rows={3}
                value={embedSnippet}
                className="flex-1 px-3 py-3 font-mono text-[11px] bg-arena-alt/50 outline-none resize-none"
                onFocus={(e) => e.target.select()}
              />
              <button
                data-testid="share-embed-copy"
                onClick={() => handleCopy(embedSnippet, "Embed code")}
                className="self-stretch px-4 bg-white border-l border-arena-line hover:bg-arena-alt transition-colors font-mono text-[10px] uppercase tracking-[0.2em]"
              >
                <Copy size={14} />
              </button>
            </div>
          </div>

          {leader && (
            <div className="border-t border-arena-line pt-4 text-[11px] font-mono text-arena-soft">
              <span className="uppercase tracking-[0.25em]">Currently leading: </span>
              <span className="font-bold" style={{ color: leader.color }}>
                {leader.name}
              </span>
              <span className={leader.pnl >= 0 ? "text-profit" : "text-loss"}>
                {" "}
                ({leader.pnl_pct >= 0 ? "+" : ""}
                {leader.pnl_pct.toFixed(2)}%)
              </span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
